import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import { makeGetRouteForLastSelectedTab } from './factories'
import { sortOrderMSServicesByHrid } from './orders'
import { NoOrderForOpportunityError } from './errors'
import type { TEntityId } from '~/types/models'
import type {
  IOpportunity,
  IOpportunityLastSelectedState,
  IOpportunityOverview,
  IOpportunityMSService,
} from '~/types/opportunity/models'
import type { IOrder, IOrderOverview } from '~/types/order/models'
import type { ITask } from '~/types/task/models'

const apiEntityPath = '/api/opportunities'

export async function getOpportunities({ apiPath = apiEntityPath } = {}) {
  const callingMn = getOpportunities.name
  return httpGet<IOpportunity[]>({ callingMn, apiPath })
}

export async function getOpportunity(id: string) {
  const callingMn = getOpportunity.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<IOpportunity>({ callingMn, apiPath })
}

// const map = new Map()

export async function getOpportunityOverview(id: string) {
  const callingMn = getOpportunityOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  const overview = await httpGet<IOpportunityOverview>({ callingMn, apiPath })
  overview.opportunityMasterServices.forEach((oms) =>
    oms.opportunityMSServices?.sort((a, b) => a.hrid.localeCompare(b.hrid))
  )
  return overview
}

export async function getLastSelectedStateForOpportunity(id: TEntityId) {
  const callingMn = getLastSelectedStateForOpportunity.name
  const apiPath = `${apiEntityPath}/${id}/lastselectedstate`
  return httpGet<IOpportunity>({ callingMn, apiPath })
}

export async function getSuppliersFromOpportunity(
  id: TEntityId,
  onlyWithReadyForContractMSServices: boolean = false
) {
  const callingMn = getSuppliersFromOpportunity.name
  const apiPath =
    `${apiEntityPath}/${id}/suppliers` +
    (onlyWithReadyForContractMSServices
      ? '?onlywithreadyforcontractmsservices=true'
      : '')
  return httpGet<IOpportunity>({ callingMn, apiPath })
}

const _getRouteForLastSelectedTab =
  makeGetRouteForLastSelectedTab<IOpportunityLastSelectedState>(
    apiEntityPath,
    'Opportunities-Overview'
  )

/**
 * ⚠ MIGRATION, and it is not optional. The Opportunity silo's third tab has been
 * renamed TWICE, so three stored values are in circulation:
 *
 *   before 2026-09-07  'Operations'  — the original tab
 *   2026-09-07..09-09  'Deals'       — the deals-only projection that replaced it
 *   from  2026-09-09   'Operations'  — this tab, now showing the ORDER
 *
 * The route segment came back to `Operations` with the order view, so the OLDEST
 * rows resolve natively again and it is the MIDDLE band that now needs remapping
 * — this function's direction is the reverse of what it was two days ago. Do not
 * "restore" it.
 *
 * The factory turns the stored value straight into a route NAME
 * (`Opportunities-Overview-${tab}-id`), so without this remap a user who last
 * stood on the Deals tab would push `Opportunities-Overview-Deals-id`, a route
 * that resolves to nothing, and `OverviewPage.applyTabFromRoute` would throw
 * `tabText is not in tabItems array` on top of it.
 *
 * Remapped at READ time rather than by migrating the stored rows: the rows are
 * server-side, one per user per opportunity, and nothing here can rewrite them.
 * Every WRITE stores 'Operations' again (SiloOverview persists
 * `this.screenTopTab`), so the stale values drain away as users visit the tab.
 *
 * Landing on Operations for an opportunity that has NO order is safe: the page's
 * overview function throws `NoOrderForOpportunityError` and the factory's
 * asyncData error path redirects to Sales.
 *
 * ⚠ OPPORTUNITY-ONLY. Companies and Home have their own Operations tabs, showing
 * something else entirely; their `getRouteForLastSelectedTab`s must keep
 * returning them untouched.
 */
export async function getRouteForLastSelectedTab(id: string) {
  const routeName = await _getRouteForLastSelectedTab(id)
  return routeName === 'Opportunities-Overview-Deals-id'
    ? 'Opportunities-Overview-Operations-id'
    : routeName
}

export function getRouteForOnCreated(id: string) {
  return { name: 'Opportunities-Overview-Sales-id', params: { id } }
}

export async function getOrderForOpportunity(id: string) {
  const callingMn = getOrderForOpportunity.name
  const apiPath = `${apiEntityPath}/${id}/order`
  return httpGet<IOrder>({ callingMn, apiPath })
}

export async function getOrderOverviewForOpportunity(id: string) {
  const callingMn = getOrderOverviewForOpportunity.name
  const apiPath = `${apiEntityPath}/${id}/orderoverview`
  return httpGet<IOrderOverview>({ callingMn, apiPath })
}

/**
 * The Opportunity Overview OPERATIONS tab's `getOverviewFunction`.
 *
 * That tab renders the ORDER card set at an *Opportunities* route, so it is
 * handed an OPPORTUNITY id and must return an `IOrderOverview`. Three things
 * separate it from the raw fetch above:
 *
 * 1. A missing order is turned into `NoOrderForOpportunityError`, which
 *    `handleAsyncDataErrors` redirects to the Sales tab. The endpoint answers
 *    `null` for an opportunity nobody has signed a contract on, and returning
 *    that would hand the factory an undefined `overviewData` to render.
 *
 * 2. The hrid sort `getOrderOverview` does is applied here too — this endpoint
 *    does not do it server-side.
 *
 * 3. ⚠ `lastSelectedState` IS STRIPPED, and that is load-bearing.
 *    `seedLastSelectedStateFromOverview` keys the row by the entity name it
 *    derives from the ROUTE — `opportunity` here — while this payload's row
 *    belongs to the ORDER, id and version and all. Seeding it would file the
 *    order's row in the opportunity's slot, and the Sales tab would then PATCH
 *    the order's row through the opportunity endpoint. Removing it sends that
 *    function down its dedicated-fetch fallback, which fetches the opportunity's
 *    own row — which the shell needs present so ITS `selectedScreenTopTabValue`
 *    write takes the update branch rather than retrying a create.
 *    See `doPersistLastSelectedState` in factoriesForPages/SiloOverview.ts for
 *    the other half of this.
 */
export async function getOrderOverviewForOperationsTab(id: string) {
  const overview = await getOrderOverviewForOpportunity(id)
  if (!overview) {
    throw new NoOrderForOpportunityError(id)
  }
  sortOrderMSServicesByHrid(overview)
  delete (overview as Partial<IOrderOverview>).lastSelectedState
  return overview
}

export async function getOpportunityMSServicesForOpportunity(id: string) {
  const callingMn = getOpportunityMSServicesForOpportunity.name
  const apiPath = `${apiEntityPath}/${id}/opportunitymsservices`
  return httpGet<IOpportunityMSService[]>({ callingMn, apiPath })
}

export async function getTasksForOpportunity(id: string) {
  const callingMn = getTasksForOpportunity.name
  const apiPath = `${apiEntityPath}/${id}/tasks`
  return httpGet<ITask[]>({ callingMn, apiPath })
}

export async function createOpportunity(entity: IOpportunity) {
  const callingMn = createOpportunity.name
  const apiPath = `/api/opportunities/`
  return httpPost<IOpportunity>({ callingMn, apiPath, entity })
}

export async function updateOpportunity(
  entityData: Partial<IOpportunity>,
  id: TEntityId
) {
  const callingMn = updateOpportunity.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIOpportunity = Partial<IOpportunity>
  return httpPatchMerge<TPartialIOpportunity>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteOpportunity(entityData: IOpportunity) {
  const callingMn = deleteOpportunity.name
  const apiPath = apiEntityPath
  return httpDelete<IOpportunity>({
    callingMn,
    apiPath,
    entityData,
  })
}
